import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, filter } from 'rxjs/operators';
import { Observable, ReplaySubject } from 'rxjs';
import { AuthConfig, OAuthEvent, OAuthService, TokenResponse } from 'angular-oauth2-oidc';
import { ConfigService } from './config.service';
import { UtilsService } from './utils.service';
import { CurrentUserService } from './current-user.service';
import { environment } from 'src/environments/environment';
import { Constants } from '../constants/Constants';
import { ROUTES } from '../constants/routes';
import { User } from '../models/user';
import { UserProfile } from '../models/user-profile';
import { AuthResponse, AuthRoleRequest } from '../models/auth';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {

  // ── Clés sessionStorage ────────────────────────────────────────────────
  private static readonly JWT_KEY  = 'jwt_token'; // JWT interne (mode REC/INT/DEV)
  private static readonly USER_KEY = 'user';       // profil utilisateur

  private isDoneLoadingSubject$ = new ReplaySubject<boolean>();
  public  isDoneLoading$        = this.isDoneLoadingSubject$.asObservable();

  private user!: User;

  public getUser(): User {
    // Déléguer au CurrentUserService (source de vérité = API backend)
    // Fallback sur this.user pour compatibilité avec le code existant
    const currentUser = this.currentUserService.getCurrentUser();
    if (currentUser) {
      return {
        lastName:     currentUser.lastName,
        firstName:    currentUser.firstName,
        identifier:   currentUser.identifier,
        profile:      currentUser.role,
        applications: currentUser.applications,
        nettingView:  currentUser.nettingView,
      } as User;
    }
    return this.user;
  }
  public setUser(user: User)   { this.user = user; return this.user; }

  constructor(
    private oauthService:  OAuthService,
    private http:          HttpClient,
    private router:        Router,
    private configService: ConfigService,
    private utilsService:  UtilsService,
    private currentUserService: CurrentUserService
  ) {
    // Restaurer le profil depuis la session au démarrage
    const stored = sessionStorage.getItem(AuthenticationService.USER_KEY);
    if (stored) {
      try { this.user = JSON.parse(stored); } catch (_) {}
    }
  }

  // ══════════════════════════════════════════════════════════════════════
  // MODE FID — OAuth2 SNCF (PROD)
  // ══════════════════════════════════════════════════════════════════════

  /**
   * Configure le service OAuth et lance la séquence de login FID.
   */
  setOAuthConfig(configService: ConfigService, oAuthService: OAuthService): Promise<any> {
    return new Promise((resolve) => {
      if (configService.isAuthEnabled()) {
        console.log('[AUTH][FID] Configuration OAuth — issuer:',
          configService.getConfiguration().issuer,
          '| redirectUri:', configService.getConfiguration().redirectUri,
          '| clientId:', configService.getConfiguration().clientId);
        const authConfig: AuthConfig = {
          issuer:              configService.getConfiguration().issuer,
          redirectUri:         configService.getConfiguration().redirectUri,
          clientId:            configService.getConfiguration().clientId,
          scope:               configService.getConfiguration().scope,
          responseType:        'code',
          dummyClientSecret:   configService.getConfiguration().client_secret,
          useHttpBasicAuth:    true, // FID SNCF : Authorization: Basic Base64[Client_ID:Client_Secret] sur /access_token
          sessionChecksEnabled: false,
        };
        oAuthService.configure(authConfig);

        // ── Log de TOUS les événements OAuth (diagnostic du flux) ────────
        this.oauthService.events.subscribe(e => {
          if (e.type === 'token_error' || e.type === 'token_refresh_error'
            || e.type === 'discovery_document_load_error'
            || e.type === 'code_error' || e.type === 'session_error') {
            console.error('[AUTH][FID][EVENT]', e.type, e);
          } else {
            console.log('[AUTH][FID][EVENT]', e.type);
          }
        });

        this.runInitialLoginSequence();
        this.oauthService.setupAutomaticSilentRefresh();
        this.oauthService.events
          .pipe(filter(e => ['token_received'].includes(e.type)))
          .subscribe(() => {
            console.log('[AUTH][FID] token_received → loadUserProfile()');
            this.oauthService.loadUserProfile();
          });
      } else {
        console.log('[AUTH] authEnable=false → mode INTERNAL (login/mdp)');
      }
      resolve(undefined);
    });
  }

  /**
   * Séquence de login initiale OAuth2.
   */
  public runInitialLoginSequence(): Promise<void> {
    console.log('[AUTH][FID] runInitialLoginSequence — chargement discovery document…');
    return this.oauthService.loadDiscoveryDocument()
      .then(() => {
        console.log('[AUTH][FID] Discovery document OK → tryLogin() (échange code→token si code présent dans l\'URL)');
        return this.oauthService.tryLogin();
      })
      .then(() => {
        console.log('[AUTH][FID] tryLogin terminé.',
          '| hasValidIdToken:', this.oauthService.hasValidIdToken(),
          '| hasValidAccessToken:', this.oauthService.hasValidAccessToken(),
          '| state:', this.oauthService.state);
        this.isDoneLoadingSubject$.next(true);
        if (this.oauthService.state
          && this.oauthService.state !== 'undefined'
          && this.oauthService.state !== 'null') {
          console.log('[AUTH][FID] State présent → navigation vers /', ROUTES.EMPTY_PATH);
          this.router.navigateByUrl(`/${ROUTES.EMPTY_PATH}`);
        }
      })
      .catch((err) => {
        console.error('[AUTH][FID] Échec runInitialLoginSequence (discovery ou tryLogin) :', err);
        this.isDoneLoadingSubject$.next(true);
      });
  }

  /**
   * Après réception du token FID, appelle /auth/me pour récupérer
   * le profil complet depuis la BDD (rôle résolu par le backend).
   *
   * ⚠️ NE PLUS appeler l'endpoint userinfo FID externe ni getUserProfile() BDD
   *    Le backend fait tout : valide le token FID + résout le rôle en BDD
   */
  public checkUserAuthorization(): void {
    console.log('[AUTH] checkUserAuthorization → appel /auth/me…');
    this.getMyProfile().subscribe({
      next: (profile) => {
        console.log('[AUTH] /auth/me OK — identifier:', profile?.identifier,
          '| role:', profile?.role);
        if (profile?.role) {
          this.saveSessionUser(profile);
          this.router.navigateByUrl(`/${ROUTES.CORE_PATH}`);
        } else {
          // Utilisateur FID valide mais pas dans la BDD Packgemini
          console.warn('[AUTH] /auth/me OK mais aucun rôle → /unauthorized');
          this.router.navigateByUrl(`/${ROUTES.UNAUTHORIZED_PATH}`);
        }
      },
      error: (err) => {
        console.error('[AUTH] Erreur récupération profil /auth/me — status:',
          err.status, '| message:', err.message ?? err.error?.message, err);
        if (err.status === 403 || err.status === 404) {
          window.alert("Vous n'êtes pas autorisé à accéder à cette application.");
        }
        console.warn('[AUTH] Échec /auth/me → déclenchement logOut()');
        this.logOut();
      }
    });
  }

  /**
   * Redirige vers la page de login FID.
   */
  public logIn(targetUrl?: string): void {
    console.log('[AUTH][FID] logIn → initCodeFlow, targetUrl =', targetUrl ?? this.router.url);
    sessionStorage.removeItem(AuthenticationService.USER_KEY);
    this.oauthService.initCodeFlow(encodeURIComponent(targetUrl ?? this.router.url));
  }

  // ══════════════════════════════════════════════════════════════════════
  // MODE INTERNAL — Login / Mot de passe (REC / INT / DEV)
  // ══════════════════════════════════════════════════════════════════════

  /**
   * Authentification par identifiant + mot de passe.
   * Stocke le JWT interne en sessionStorage puis charge le profil via /auth/me.
   *
   * Usage dans le composant login :
   *   this.authService.loginWithPassword(id, pwd).subscribe({
   *     next: () => this.router.navigate(['/app']),
   *     error: (err) => this.errorMsg = err.message
   *   });
   */
  public loginWithPassword(identifier: string, password: string): Observable<User> {
    return new Observable(observer => {
      this.http.post<any>(
        `${environment.backendApiUrl}/auth/login`,
        { identifier: identifier.toUpperCase(), password }
      ).subscribe({
        next: (response) => {
          if (response?.success && response?.token) {
            // Stocker le JWT interne → l'intercepteur l'utilisera automatiquement
            //sessionStorage.setItem(AuthenticationService.JWT_KEY, response.token);
            this.setTokenToSession(response.token);
            // L'intercepteur ajoutera Authorization: Bearer <jwt_interne> automatiquement
            this.saveSessionUser(response.user);       
            observer.next(this.getUser());
            observer.complete();
          } else {
            observer.error({ message: response?.message ?? 'Authentification échouée' });
          }
        },
        error: (err) => {
          const msg = err.error?.message ?? err.message ?? 'Erreur de connexion';
          observer.error({ message: msg, status: err.status });
        }
      });
    });
  }

  public setTokenToSession(token: string): void {
    sessionStorage.setItem(AuthenticationService.JWT_KEY, token);
  }

  // ══════════════════════════════════════════════════════════════════════
  // COMMUN — /auth/me
  // ══════════════════════════════════════════════════════════════════════

  /**
   * Récupère le profil complet de l'utilisateur connecté depuis le backend.
   * Le backend résout le rôle depuis la BDD — source de vérité absolue.
   *
   * L'Authorization: Bearer est ajouté automatiquement par l'intercepteur.
   * Utilisé dans les deux modes (FID et INTERNAL).
   *
   * Réponse backend :
   * {
   *   id, identifier, firstName, lastName, email,
   *   role: "Administrateur" | "Acteur CDS" | ...,
   *   nettingView: boolean,
   *   applications: [...]
   * }
   */
  public getMyProfile(): Observable<any> {
    return this.http
      .get<any>(`${environment.backendApiUrl}/auth/me`)
      .pipe(catchError(this.utilsService.handleError));
  }

  /**
   * Retourne le token Bearer à utiliser pour les appels API.
   * Utilisé par l'intercepteur HTTP.
   *
   * Mode FID     : id_token signé par l'IDP SNCF
   *                → getIdToken() et NON getIdentityClaims()['id_token']
   *                  (getIdentityClaims() retourne les claims parsés, pas le JWT brut)
   *
   * Mode INTERNAL: JWT interne stocké en sessionStorage après login/mdp
   */
  public getBearerToken(): string | null {
    // ── Mode FID ──────────────────────────────────────────────────────────
    if (this.configService.isAuthEnabled()) {
      const idToken = this.oauthService.getIdToken();
      if (idToken) return idToken;
      console.warn('[AUTH][FID] getBearerToken — aucun id_token disponible '
        + '(expiré ou purgé) → requête partira SANS Authorization');
      return null; // token FID expiré → intercepteur laisse passer, 401 géré
    }

    // ── Mode INTERNAL ─────────────────────────────────────────────────────
    return sessionStorage.getItem(AuthenticationService.JWT_KEY);
  }

  // ══════════════════════════════════════════════════════════════════════
  // SESSION
  // ══════════════════════════════════════════════════════════════════════

  /**
   * Sauvegarde le profil retourné par /auth/me dans la session.
   *
   * ⚠️ Le rôle stocké ici sert UNIQUEMENT à l'affichage UI (show/hide boutons).
   *    Le vrai contrôle d'accès est @PreAuthorize côté backend.
   *    Si quelqu'un modifie sessionStorage.role → le backend retourne 403.
   */
  public saveSessionUser(profile: any): void {
    this.user = {
      lastName:     profile.lastName     ?? '',
      firstName:    profile.firstName    ?? '',
      identifier:   profile.identifier   ?? '',
      profile:      profile.role         ?? '',  // ex: "Administrateur", "Acteur CDS"
      applications: profile.applications ?? [],
      nettingView:  profile.nettingView   ?? false,
    } as User;

    sessionStorage.setItem(AuthenticationService.USER_KEY, JSON.stringify(this.user));
    localStorage.setItem('debugMode', 'false');

    // ✅ Mettre à jour aussi le CurrentUserService (BehaviorSubject)
    this.currentUserService.setCurrentUser({
      id:           profile.id           ?? '',
      identifier:   profile.identifier   ?? '',
      firstName:    profile.firstName    ?? '',
      lastName:     profile.lastName     ?? '',
      email:        profile.email        ?? '',
      role:         profile.role         ?? '',
      nettingView:  profile.nettingView   ?? false,
      applications: profile.applications ?? [],
    });
  }

  /**
   * Déconnexion complète.
   *
   * Mode FID : on capture l'id_token AVANT tout nettoyage et on le passe
   * explicitement en id_token_hint — OpenAM SNCF exige ce paramètre sur
   * /connect/endSession, et la lib ne peut pas le fournir si le
   * sessionStorage a déjà été vidé.
   */
  public logOut(): void {
    console.log('[AUTH] logOut() appelé — mode FID:', this.configService.isAuthEnabled());

    if (this.configService.isAuthEnabled()) {
      // ── 1. Capturer l'id_token AVANT toute purge ──────────────────────
      const idToken = this.oauthService.getIdToken();
      console.log('[AUTH][FID] logOut — id_token présent:', !!idToken,
        idToken ? `(longueur ${idToken.length})` : '(absent du storage !)');

      // ── 2. Nettoyer uniquement les clés applicatives ──────────────────
      //     (PAS sessionStorage.clear() : la lib OAuth y stocke ses tokens)
      sessionStorage.removeItem(AuthenticationService.JWT_KEY);
      sessionStorage.removeItem(AuthenticationService.USER_KEY);
      localStorage.removeItem('debugMode');

      // ── 3. Logout OAuth2 avec id_token_hint explicite ─────────────────
      if (idToken) {
        console.log('[AUTH][FID] logOut → redirection endSession avec id_token_hint explicite');
        this.oauthService.logOut({ id_token_hint: idToken });
      } else {
        // Pas d'id_token (session déjà morte) → nettoyage local seulement,
        // inutile d'appeler endSession qui répondrait bad_request
        console.warn('[AUTH][FID] logOut — pas d\'id_token, purge locale sans appel endSession');
        this.oauthService.logOut(true); // true = pas de redirection vers l'IDP
        this.clearSessionStorage();
        this.router.navigateByUrl(`/${ROUTES.EMPTY_PATH}`);
      }
    } else {
      // ── Mode INTERNAL : pas d'IDP, purge complète ───────────────────
      console.log('[AUTH] logOut — mode INTERNAL, purge session complète');
      this.clearSessionStorage();
    }
  }

  public clearSessionStorage(): void {
    sessionStorage.removeItem(AuthenticationService.JWT_KEY);
    sessionStorage.removeItem(AuthenticationService.USER_KEY);
    localStorage.removeItem('debugMode');
    sessionStorage.clear(); 
  }


  public setUserRole(authRoleRequest: AuthRoleRequest): Observable<AuthResponse> {
   return  this.http.patch<any>(
      `${environment.backendApiUrl}/user/set-role`, 
      authRoleRequest
    );
  }



  public isUserAdmin(): boolean {
    return this.currentUserService.isAdmin();
  }

  public isServiceManager(): boolean {
    return this.currentUserService.hasRole(UserProfile.SERVICE_MANAGER);
  }

  public isUserNotServiceManager(): boolean {
    const user = this.currentUserService.getCurrentUser();
    return !!user?.role && user.role !== UserProfile.SERVICE_MANAGER;
  }

  public isUserAdminOrServiceManager(): boolean {
    const role = this.currentUserService.getCurrentUser()?.role;
    return !!role && [
      UserProfile.ADMIN,
      UserProfile.SERVICE_MANAGER,
      UserProfile.MANAGER_CDS,
      UserProfile.GUEST_CDS,
      UserProfile.GUEST_SNCF
    ].includes(role as UserProfile);
  }

  public isUserAdminOrServiceManagerOrManagerCDS(): boolean {
    const role = this.currentUserService.getCurrentUser()?.role;
    return !!role && [
      UserProfile.ADMIN,
      UserProfile.SERVICE_MANAGER,
      UserProfile.MANAGER_CDS
    ].includes(role as UserProfile);
  }

  // ══════════════════════════════════════════════════════════════════════
  // TOKENS OAuth2 — délégation à OAuthService
  // ══════════════════════════════════════════════════════════════════════

  public hasValidAccessToken(): boolean          { return this.oauthService.hasValidAccessToken(); }
  public hasValidIdToken(): boolean              { return this.oauthService.hasValidIdToken(); }
  public refreshToken(): Promise<TokenResponse>  { return this.oauthService.refreshToken(); }
  public getAuthorizationHeader(): string        { return this.oauthService.authorizationHeader(); }
  public getIdToken(): string                    { return this.oauthService.getIdToken(); }
  public getAccessToken(): string                { return this.oauthService.getAccessToken(); }
  public silentRefresh(): Promise<OAuthEvent>    { return this.oauthService.silentRefresh(); }
  public getIdentityClaims(): any                { return this.oauthService.getIdentityClaims(); }
  public getDebugMode(): boolean {
    return JSON.parse(localStorage.getItem('debugMode') ?? 'false');
  }

  // ══════════════════════════════════════════════════════════════════════
  // MÉTHODES DÉPRÉCIÉES — conservées pour compatibilité descendante
  // ══════════════════════════════════════════════════════════════════════

  /**
   * @deprecated Utiliser checkUserAuthorization() qui appelle /auth/me
   * Ancienne méthode qui appelait l'endpoint userinfo FID externe
   */
  public getUserInfo(accessToken: string): Observable<any> {
    console.warn('[AUTH] getUserInfo() déprécié — utiliser checkUserAuthorization()');
    const headers = { 'Authorization': `Bearer ${accessToken}` };
    return this.http.get<any>(
      this.configService.getConfiguration().userInfoEndPoint,
      { headers }
    ).pipe(catchError(this.utilsService.handleError));
  }

  /**
   * @deprecated Utiliser getMyProfile() qui appelle /auth/me
   * Ancienne méthode qui cherchait le profil via /users/identifier/{login}
   */
  public getUserProfile(login: string): Observable<any> {
    console.warn('[AUTH] getUserProfile() déprécié — utiliser getMyProfile()');
    return this.http.get<any>(
      `${environment.backendApiUrl}${Constants.ApiPaths.USERS_BY_IDENTIFIER}${login}`
    ).pipe(catchError(this.utilsService.handleError));
  }

  /**
   * @deprecated Utiliser saveSessionUser(profile) avec le profil de /auth/me
   */
  public saveSessionUserLegacy(response: any, user: User, roles: string[]): void {
    console.warn('[AUTH] saveSessionUser() legacy déprécié');
    this.saveSessionUser({
      ...user,
      role: user.profile
    });
  }
}